/**
 * Shared geometric helpers for trash service-area checks.
 *
 * This is the SINGLE source of truth for the coordinate-order normalization
 * (`normalizePolygonOrder`), the ray-casting point-in-polygon test
 * (`isPointInPolygon`), and the serviceability decision
 * (`isPropertyServiceable` / `isPointServiceable`). Both the server
 * (`server/services/trash/trashGeo.ts`) and the client
 * (`client/src/components/trash-coverage-map.tsx`) re-export from here so the
 * two sides can never drift.
 *
 * Uses the ray-casting algorithm (Jordan curve theorem) to test whether a
 * lat/lng point falls inside a polygon. No third-party libraries required.
 */

/** A service-area route polygon. `polygon` vertices are [lat, lng] pairs. */
export interface ServiceAreaRoute {
  id: string | number;
  name: string;
  /** Polygon vertices as [lat, lng] pairs. */
  polygon: Array<[number, number]>;
}

/**
 * Ray-casting point-in-polygon test.
 * `polygon` must be in [lat, lng] order (call normalizePolygonOrder first).
 * Returns true when the point is strictly inside (or on the boundary of) the
 * polygon.
 */
export function isPointInPolygon(
  lat: number,
  lng: number,
  polygon: Array<[number, number]>,
): boolean {
  const n = polygon.length;
  if (n < 3) return false;

  let inside = false;
  let j = n - 1;
  for (let i = 0; i < n; i++) {
    const [yi, xi] = polygon[i];
    const [yj, xj] = polygon[j];
    const intersects =
      yi > lat !== yj > lat &&
      lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi;
    if (intersects) inside = !inside;
    j = i;
  }
  return inside;
}

/**
 * Normalise polygon coordinate order to [lat, lng].
 *
 * The ray-casting algorithm expects the first element of each vertex to be
 * latitude. Some providers (and all GeoJSON) use [lng, lat] order instead.
 *
 * Detection: a latitude is always in [-90, 90]; a longitude can range to
 * [-180, 180]. The old |first| > 90 heuristic only fired for western US
 * longitudes (e.g. -118) and silently FAILED for the eastern US, whose
 * longitudes (Atlanta -84, Miami -80, New York -74) sit inside [-90, 90] —
 * so an east-coast [lng, lat] polygon was never swapped and the point test
 * ran on transposed coordinates. We instead use the continental-US sign
 * convention, which is unambiguous across the whole map: latitudes are
 * positive (~24..49) and longitudes are negative (~-65..-125). So:
 *   - first element outside [-90, 90]  → it must be a longitude  → swap.
 *   - both inside [-90, 90] but first is negative and second is positive
 *     → the pair is [lng(neg), lat(pos)]                          → swap.
 *   - otherwise it is already [lat, lng]                          → keep.
 */
export function normalizePolygonOrder(
  polygon: Array<[number, number]>,
): Array<[number, number]> {
  if (polygon.length === 0) return polygon;
  const [first, second] = polygon[0];
  const firstCannotBeLatitude = Math.abs(first) > 90;
  const looksLikeLngLat = firstCannotBeLatitude || (first < 0 && second > 0);
  if (looksLikeLngLat) {
    return polygon.map(([lng, lat]) => [lat, lng] as [number, number]);
  }
  return polygon;
}

/**
 * Check whether a lat/lng falls inside any of the provider's service-area
 * route polygons.
 *
 * Returns `{ serviceable: true, routeId: <id> }` for the first matching route,
 * or `{ serviceable: false }` when no polygon contains the point.
 *
 * Handles missing/malformed polygon arrays gracefully (skips the route).
 * Normalises polygon coordinate order before testing so both [lat,lng] and
 * [lng,lat] provider formats work correctly.
 */
export function isPropertyServiceable(
  lat: number,
  lng: number,
  routes: ServiceAreaRoute[],
): { serviceable: boolean; routeId?: string | number } {
  for (const route of routes) {
    if (!Array.isArray(route.polygon) || route.polygon.length < 3) continue;
    const normalized = normalizePolygonOrder(route.polygon);
    if (isPointInPolygon(lat, lng, normalized)) {
      return { serviceable: true, routeId: route.id };
    }
  }
  return { serviceable: false };
}

/** True when the lat/lng falls inside any of the service-area route polygons. */
export function isPointServiceable(
  lat: number,
  lng: number,
  routes: ServiceAreaRoute[],
): boolean {
  return isPropertyServiceable(lat, lng, routes).serviceable;
}
